import React, { useState, useMemo, useEffect, useRef } from "react";
import { useBar } from "../../../../context/BarContext";
import BarOwnerArtistCard from "../BarOwnerArtistCard";
import ManagementArtistProfileModal from "../management/ManagementArtistProfileModal";
import { useBarOwnerData } from "../../../../context/BarOwnerDataContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import { artistGenresList } from "../../../../utils/genres";

const MyArtists = ({
  artists = [],
  associations = {},
  loading = false,
  onRefetch,
  activeTab,
  selectedBar,
  isMapsLoaded = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [selectedArtist, setSelectedArtist] = useState(null);
  const { userRole } = useUserRole();
  const { invalidateCache, fetchManagementArtists } = useBarOwnerData();
  const previousActiveTabRef = useRef(null);

  // Reset filters when tab becomes active
  useEffect(() => {
    if (
      activeTab === "my-artists" &&
      previousActiveTabRef.current !== "my-artists"
    ) {
      setSearchQuery("");
      setSelectedLetter("");
    }
    previousActiveTabRef.current = activeTab;
  }, [activeTab]);

  // Alphabet for filtering
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  // Sort artists alphabetically
  const sortedArtists = useMemo(() => {
    return [...artists].sort((a, b) => {
      const nameA = a.stage_name || "";
      const nameB = b.stage_name || "";
      return nameA.localeCompare(nameB);
    });
  }, [artists]);

  // Filter artists based on search and alphabet selection
  const filteredArtists = useMemo(() => {
    let filtered = sortedArtists;

    // Apply alphabet filter
    if (selectedLetter) {
      filtered = filtered.filter((artist) =>
        (artist.stage_name || "").toUpperCase().startsWith(selectedLetter)
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((artist) => {
        const stageName = (artist.stage_name || "").toLowerCase();
        const hometown = (artist.hometown || "").toLowerCase();
        const genreMatch = artistGenresList(artist).some((g) =>
          (g || "").toLowerCase().includes(query)
        );
        return (
          stageName.includes(query) ||
          hometown.includes(query) ||
          genreMatch
        );
      });
    }

    return filtered;
  }, [sortedArtists, selectedLetter, searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // Clear alphabet filter when searching
    if (e.target.value.trim()) {
      setSelectedLetter("");
    }
  };

  const handleLetterClick = (letter) => {
    setSelectedLetter(selectedLetter === letter ? "" : letter);
    // Clear search when using alphabet filter
    setSearchQuery("");
  };

  const handleArtistClick = (artist) => {
    setSelectedArtist(artist);
  };

  const handleRefresh = () => {
    invalidateCache("managementArtists");
    fetchManagementArtists();
    if (typeof onRefetch === "function") onRefetch();
  };

  if (!selectedBar) {
    return (
      <div className="artists-content">
        <div className="no-bar-message">
          <h3>No Bar Selected</h3>
          <p>Please select a bar to view artists.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="artists-content">
      {/* Search Section */}
      <div className="search-section">
        <div className="search-bar-container">
          <input
            type="text"
            placeholder="Search artists by name, genre, or location..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        {/* Alphabet Filter */}
        <div className="alphabet-filter">
          <div className="alphabet-buttons">
            {alphabet.map((letter) => (
              <button
                key={letter}
                className={`alphabet-btn ${
                  selectedLetter === letter ? "active" : ""
                }`}
                onClick={() => handleLetterClick(letter)}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="results-info">
        <span className="results-count">
          {filteredArtists.length} artist
          {filteredArtists.length !== 1 ? "s" : ""}
          {selectedLetter && ` starting with "${selectedLetter}"`}
          {searchQuery && ` matching "${searchQuery}"`}
        </span>
      </div>

      {/* Artists Grid */}
      <div className="bars-grid bars-grid--artists">
        {loading ? (
          <div
            className="chat-messages-loading"
            style={{
              gridColumn: "1 / -1",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "3rem",
              minHeight: "400px",
              width: "100%",
            }}
          >
            <div className="spinner" aria-hidden="true" />
            <p
              style={{
                color: "var(--text-secondary)",
                margin: 0,
                marginTop: "1rem",
              }}
            >
              Loading artists…
            </p>
          </div>
        ) : filteredArtists.length > 0 ? (
          filteredArtists.map((artist) => {
            const assocs = associations[artist.artist_id] || [];
            const hasPendingForThisBar = assocs.some(
              (a) => a.bar_id === selectedBar?.bar_id && a.status === "pending"
            );
            return (
              <BarOwnerArtistCard
                key={artist.artist_id}
                artist={artist}
                onClick={() => handleArtistClick(artist)}
                hasPending={hasPendingForThisBar}
              />
            );
          })
        ) : (
          <div className="no-results" style={{ gridColumn: "1 / -1" }}>
            <div className="no-results-icon">🎤</div>
            <h3>No artists found</h3>
            <p>
              {searchQuery
                ? `No artists match "${searchQuery}"`
                : selectedLetter
                ? `No artists start with "${selectedLetter}"`
                : artists.length === 0
                ? "No artists have been added to this bar yet. Use the 'Find Artists' tab to search for and add artists."
                : "Try adjusting your search or filters"}
            </p>
          </div>
        )}
      </div>

      {selectedArtist && (
        <ManagementArtistProfileModal
          isOpen={!!selectedArtist}
          onClose={() => setSelectedArtist(null)}
          artist={selectedArtist}
          associations={associations[selectedArtist.artist_id] || []}
          selectedBar={selectedBar}
          isMapsLoaded={isMapsLoaded}
          onRefresh={handleRefresh}
          manageBarSelection={
            userRole === "bar_admin" ? "context" : "all"
          }
          contextBarId={selectedBar?.bar_id}
        />
      )}
    </div>
  );
};

export default MyArtists;
